import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, message, Modal, Progress, Row, Tooltip } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import debounce from 'lodash/debounce';
import moment from 'moment';
import {
	appendInternalNotesForSupplier,
	getSupplierMeetingToken,
	getSupplierOWLiveShortenedLink,
} from '../../thunks/work_orders_thunks';
import { fetchInternalNotesSubscribersForSupplier } from '../../thunks/work_orders_subscribers_thunks';
import { receiveLoadingMessage } from '../../actions/messages_actions';
import {
	compareStrings,
	getObjectValues,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { imageMimeTypes, imageUploadValidation } from '../../utils/ImageUtils';
import { parseMessageToElements } from '../../utils/ChatUtils';
import { getInternalNotesChannelId } from '../../utils/WorkOrderUtils';
import { workOrderChatUploadCustomRequest } from '../../thunks/file_thunks';
import { formatUserContact } from '../../utils/DataFormatterUtils';
import { getNewNoteType } from '../../utils/WebSocketUtils';
import ChatUploader from '../ChatInput/ChatUploader';
import VoiceNoteRecorder from '../ChatInput/VoiceNoteRecorder';
import { ImageThumbnails } from '../image_thumbnails/ImageThumbnails';
import NewNoteDisplay from '../buyer_new_work_order_note_form/NewNoteDisplay';
import useDraft from '../../hooks/useDraft';
import { buildWorkOrderNoteDraftIdentity } from '../../utils/WorkOrderNoteDrafts';

const NOTE_DRAFT_CHANNEL = 'internal';

const videoMimeTypes = [
	'video/3gpp',
	'video/mp4',
	'video/mpeg',
	'video/ogg',
	'video/quicktime',
	'video/webm',
	'video/x-m4v',
	'video/ms-asf',
	'video/x-ms-wmv',
	'video/x-msvideo',
];

export interface InternalWorkspaceWorkOrderNoteComposerProps {
	workOrder: any;
	workOrderId: string;
	typingDetails: any[];
	newNoteCount: number;
	onTyping: () => void;
	onSendNote: (text?: string, newNoteType?: string) => void;
	onRefresh: (wo?: any) => void;
	history?: any;
	currentUser: any;
	errors: string[];
	token: string;
	appendWorkOrderNotes: (payload: any) => Promise<any>;
	receiveLoadingMessage: (channelId: any, entity: any) => void;
	getMeetingToken: (roomName: string) => Promise<any>;
	owShortenedLink: (longUrl: string) => Promise<any>;
	fetchSupplierUsers: (search: string, woId: any, params: any) => Promise<any>;
}

const InternalWorkspaceWorkOrderNoteComposer = ({
	workOrder,
	workOrderId,
	typingDetails,
	newNoteCount,
	onTyping,
	onSendNote,
	onRefresh,
	currentUser,
	errors,
	token,
	appendWorkOrderNotes,
	receiveLoadingMessage,
	getMeetingToken,
	owShortenedLink,
	fetchSupplierUsers,
}: InternalWorkspaceWorkOrderNoteComposerProps) => {
	const draftIdentity = useMemo(
		() => buildWorkOrderNoteDraftIdentity(currentUser, workOrderId, NOTE_DRAFT_CHANNEL),
		[currentUser, workOrderId]
	);
	const noteTextRef = useRef('');
	const { restored: restoredDraftBody } = useDraft({
		value: () => noteTextRef.current,
		identity: draftIdentity,
	});

	const [noteText, setNoteText] = useState(restoredDraftBody || '');
	noteTextRef.current = noteText;
	const [photoFileList, setPhotoFileList] = useState<any[]>([]);
	const [photoLinks, setPhotoLinks] = useState<string[]>([]);
	const [photoPreviewModalVisible, setPhotoPreviewModalVisible] = useState(false);
	const [videoFileList, setVideoFileList] = useState<any[]>([]);
	const [videoLink, setVideoLink] = useState('');
	const [videoPreviewModalVisible, setVideoPreviewModalVisible] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const [uploadingFileCount, setUploadingFileCount] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);
	const sendMessage = useCallback(
		(JSONString: string) => {
			const entity = JSON.parse(JSONString);

			if (nullSafeGet('noteType', entity) === 'heartbeat') {
				return;
			}
			appendWorkOrderNotes({ id: parseInt(workOrderId, 10), note: entity }).then(() => {
				onRefresh();
				onSendNote(entity.text || undefined, getNewNoteType(entity));
				if (!entity.text) {
					const element = document.getElementById('internalWorkspaceChatBox');
					if (element) {
						element.scrollTop = element.scrollHeight;
					}
				}
			});
		},
		[appendWorkOrderNotes, onSendNote, workOrder]
	);

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			const trimmed = noteText && noteText.trim ? noteText.trim() : '';
			if (!trimmed) {
				message.error("Whoops! You can't send an empty note.");
				return;
			}

			const { elements, text } = parseMessageToElements(noteText);
			const entity = {
				text,
				noteAddedBy: currentUser.email,
				noteAddedByContact: currentUser,
				noteAddedAt: moment.utc(),
				elements,
			};
			receiveLoadingMessage(getInternalNotesChannelId(workOrderId), entity);
			setTimeout(() => {
				const element = document.getElementById('internalWorkspaceChatBox');
				if (element) {
					element.scrollTop = element.scrollHeight;
				}
			}, 50);

			sendMessage(JSON.stringify(entity));
			setNoteText('');
		},
		[currentUser, noteText, receiveLoadingMessage, sendMessage, workOrder]
	);

	const handlePhotoUploadChange = useCallback((info: any) => {
		setPhotoFileList(info.fileList);
		if (info.fileList.some((file: any) => compareStrings(file.status, 'uploading'))) {
			const activeFiles = info.fileList.filter(
				(file: any) => file.status === 'uploading' || file.status === 'done'
			);
			const totalFileCount = activeFiles.length;

			const totalProgress = activeFiles.reduce((sum: number, file: any) => {
				const progress = file.status === 'done' ? 100 : nullSafeGetOrElse('percent', file, 0);
				return sum + progress;
			}, 0);
			const averageProgress = totalFileCount > 0 ? Math.round(totalProgress / totalFileCount) : 0;

			setFileUploading(true);
			setUploadingFileCount(totalFileCount);
			setUploadProgress(averageProgress);
			return;
		}
		if (info.fileList.every((file: any) => compareStrings(file.status, 'done'))) {
			const links = info.fileList.map((file: any) => nullSafeGetOrElse('response.data', file, ''));
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setPhotoLinks(links || []);
			setPhotoPreviewModalVisible(true);
		}
	}, []);

	const handleVideoUploadChange = useCallback((info: any) => {
		setVideoFileList(info.fileList);
		if (info.file.status === 'uploading') {
			const progress = nullSafeGetOrElse('file.percent', info, 0);
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(Math.round(progress));
			return;
		}
		if (info.file.status === 'done') {
			const vLink = nullSafeGetOrElse('file.response.data', info, '');
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setVideoLink(vLink);
			setVideoPreviewModalVisible(true);
		}
	}, []);

	const onPhotoSubmit = useCallback(() => {
		const entities = photoLinks.map((photoLink) => ({
			text: '',
			photo: photoLink,
			noteAddedBy: currentUser.email,
			noteAddedAt: moment.utc(),
			noteAddedByContact: currentUser,
		}));
		entities.forEach((entity) => sendMessage(JSON.stringify(entity)));
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, [currentUser, photoLinks, sendMessage]);

	const onPhotoCancel = useCallback(() => {
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, []);

	const onVideoSubmit = useCallback(() => {
		const entity = {
			text: '',
			video: videoLink,
			noteAddedBy: currentUser.email,
			noteAddedAt: moment.utc(),
			noteAddedByContact: currentUser,
		};
		sendMessage(JSON.stringify(entity));
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, [currentUser, sendMessage, videoLink]);

	const onVideoCancel = useCallback(() => {
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, []);

	const handleSendAudio = useCallback(
		(blob: Blob, file: File) => {
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(0);

			workOrderChatUploadCustomRequest({
				userType: currentUser.userType,
				workOrderId: workOrderId,
				file: file,
				onProgress: ({ percent }) => {
					setUploadProgress(percent);
				},
				onSuccess: ({ data: audioLink }) => {
					setFileUploading(false);
					setUploadingFileCount(0);
					setUploadProgress(0);
					const entity = {
						text: '',
						audio: audioLink,
						noteAddedBy: currentUser.email,
						noteAddedAt: moment.utc(),
						noteAddedByContact: currentUser,
					};
					sendMessage(JSON.stringify(entity));
				},
				onError: () => {
					setFileUploading(false);
					setUploadingFileCount(0);
					setUploadProgress(0);
				},
				source: 'VoiceNoteRecorder',
			});
		},
		[currentUser, sendMessage, workOrderId]
	);

	const handleStartVideoCall = useCallback(() => {
		getMeetingToken(
			`${nullSafeGet('buyerCompanyId', workOrder)}-${nullSafeGet('id', workOrder)}`
		).then((meetingId) => {
			const longUrl = `https://live.useopenwrench.com/?meetingid=${meetingId}`;
			owShortenedLink(longUrl).then((url) => {
				const entity = {
					text: `${currentUser.nameGiven} just went live. Join the video call with them at ${url}`,
					noteAddedBy: currentUser.email,
					noteAddedAt: moment.utc(),
					noteAddedByContact: currentUser,
				};
				sendMessage(JSON.stringify(entity));
			});
		});
	}, [currentUser, getMeetingToken, owShortenedLink, sendMessage, workOrder]);

	const loadUsers = useCallback(
		(key: string, cb: (results: any[]) => void) => {
			const count = key.split(' ').length - 1;
			if (count < 2) {
				fetchSupplierUsers(key, workOrderId, {}).then((data) =>
					cb(
						getObjectValues(data)
							.map((el) => formatUserContact(el))
							.sort((a, b) => {
								const ai = a.status === 'inactive' ? 1 : 0;
								const bi = b.status === 'inactive' ? 1 : 0;
								return ai - bi;
							})
					)
				);
			} else {
				cb([]);
			}
		},
		[fetchSupplierUsers, workOrderId]
	);

	const debounceloadUsers = useMemo(() => debounce(loadUsers, 250), [loadUsers]);

	const onSearch = useCallback(
		(search: string, cb: (results: any[]) => void) => {
			debounceloadUsers(search, cb);
		},
		[debounceloadUsers]
	);

	const beforeVideoUpload = useCallback((file: any) => {
		const isVideo = videoMimeTypes.indexOf(file.type) !== -1;
		if (!isVideo) {
			message.error("This file either isn't a video or is not in a file type we support.");
		}
		return isVideo;
	}, []);

	const multiplePhotos = nullSafeGetOrElse('length', photoLinks, 1) > 1;

	const owLiveHref = `https://live.useopenwrench.com/?roomName=${nullSafeGet(
		'facility.buyerCompanyId',
		currentUser
	)}-${workOrderId}&authToken=${token}&userType=${currentUser.userType}`;

	return (
		<form
			onSubmit={handleSubmit}
			className="internal-workspace-note-composer w-full min-w-0 max-w-full overflow-hidden"
		>
			{errors.length > 0
				? errors.map((error, idx) => (
						<Alert key={idx} message={error} type="error" className="mb-2" />
				  ))
				: null}
			<Modal
				visible={photoPreviewModalVisible}
				width={600}
				title={multiplePhotos ? 'Send these photos?' : 'Send this photo?'}
				okText="Send"
				onCancel={onPhotoCancel}
				onOk={onPhotoSubmit}
				closable={false}
			>
				<Row className="flex flex-wrap gap-3">
					{photoLinks.map((photoLink) => (
						<ImageThumbnails
							key={photoLink}
							imageWidth={100}
							imageHeight={100}
							photoStrings={[photoLink]}
						/>
					))}
				</Row>
			</Modal>
			<Modal
				visible={videoPreviewModalVisible}
				width={600}
				title="Send this video?"
				okText="Send"
				onCancel={onVideoCancel}
				onOk={onVideoSubmit}
				closable={false}
			>
				<video width="100%" height="auto" controls>
					<source src={videoLink} type={getFileMimeTypeFromString(videoLink)} />
					Your browser does not support the video tag.
				</video>
			</Modal>
			<NewNoteDisplay onRefresh={onRefresh} newNoteCount={newNoteCount} />
			{fileUploading ? (
				<div className="bodyregular mx-auto flex w-1/3 items-center justify-between self-center rounded-3xl rounded-b-none bg-owgray px-4 py-2">
					<span>
						{`Uploading ${uploadingFileCount} ${uploadingFileCount === 1 ? 'file' : 'files'}`}
					</span>
					<Progress type="circle" percent={uploadProgress} width={30} />
				</div>
			) : null}
			{/* Figma footer: 450×66, pt-s pr-2xs pb-s pl-2xs, gap 10px, 1px top border */}
			<div className="mx-auto box-border flex w-full min-w-0 shrink-0 flex-row items-center gap-2.5 border-0 border-t border-solid border-grey-5 bg-white px-3 py-s">
				<div className="flex shrink-0 flex-row items-center gap-1">
					<a href={owLiveHref} target="_blank" rel="noreferrer" className="leading-none">
						<Tooltip title="Start video call">
							<button
								type="button"
								onClick={handleStartVideoCall}
								className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-character-disabled bg-white text-character-primary"
							>
								<i className="icons8-font icons8-video-call text-lg leading-none" />
							</button>
						</Tooltip>
					</a>
					<ChatUploader
						name="file"
						multiple={true}
						showUploadList={false}
						beforeUpload={imageUploadValidation}
						onChange={handlePhotoUploadChange}
						id={workOrderId}
						accept={imageMimeTypes}
						fileList={photoFileList}
					>
						<Tooltip title="Upload photo">
							<button
								type="button"
								className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-character-disabled bg-white text-character-primary"
							>
								<i className="icons8-font icons8-compact-camera text-lg leading-none" />
							</button>
						</Tooltip>
					</ChatUploader>
					<ChatUploader
						name="file"
						showUploadList={false}
						beforeUpload={beforeVideoUpload}
						onChange={handleVideoUploadChange}
						accept={videoMimeTypes}
						id={workOrderId}
						fileList={videoFileList}
					>
						<Tooltip title="Upload video">
							<button
								type="button"
								className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-character-disabled bg-white text-character-primary"
							>
								<i className="icons8-font icons8-video text-lg leading-none" />
							</button>
						</Tooltip>
					</ChatUploader>
				</div>
				<div className="flex min-h-0 min-w-0 flex-1 items-center">
					<VoiceNoteRecorder
						value={noteText}
						onChange={setNoteText}
						onTyping={onTyping}
						handleSubmit={handleSubmit}
						onSearch={onSearch}
						onSendAudio={handleSendAudio}
						typingDetails={typingDetails}
						inputPlaceholder="Type a reply..."
						inputContainerClassName="min-w-0 flex-1"
						micContainerClassName="flex items-center justify-center pl-2"
						hideTypingDetails
						inputClassName="!border !border-solid !border-character-disabled"
						micButtonClassName="!border !border-solid !border-character-disabled"
						suggestionsPortalHost={document.body}
					/>
				</div>
			</div>
		</form>
	);
};

const mapStateToProps = (state: any, ownProps: any) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	errors: state.session.errors,
	token: state.session.token,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
	appendWorkOrderNotes: (payload: any) => dispatch(appendInternalNotesForSupplier(payload)),
	receiveLoadingMessage: (channelId: any, msg: any) =>
		dispatch(receiveLoadingMessage(channelId, msg)),
	getMeetingToken: (roomName: string) => dispatch(getSupplierMeetingToken(roomName)),
	owShortenedLink: (longUrl: string) => dispatch(getSupplierOWLiveShortenedLink(longUrl)),
	fetchSupplierUsers: (search: string, woId: any, params: any) =>
		dispatch(fetchInternalNotesSubscribersForSupplier(search, woId, params)()),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(InternalWorkspaceWorkOrderNoteComposer)
);
